import type { TransferRouteConfig } from "@majourneys/shared";

/**
 * The hub's ride tiles (owner approved, 2026-09-29): seven ways into the
 * booking, each backed by one or more real routes from the transfers API.
 * A tile knows its routes by slug (without the city prefix); prices always
 * come from the API.
 */
export type TileKind = "airport" | "city" | "restaurant" | "night" | "agafay" | "cities" | "driver";

export const TILE_KINDS: readonly TileKind[] = ["airport", "city", "restaurant", "night", "agafay", "cities", "driver"];

export const TILE_ICONS: Record<TileKind, string> = {
  airport: "ri-plane-line",
  city: "ri-building-line",
  restaurant: "ri-restaurant-line",
  night: "ri-music-2-line",
  agafay: "ri-fire-line",
  cities: "ri-road-map-line",
  driver: "ri-steering-2-line",
};

/**
 * Each tile's routes, bare slugs, in the order its first line offers them
 * (Other cities: the "Going to" chips; driver: half day then full day).
 * A route the API does not return is left out; a route no tile names goes
 * to Other cities, so every ride the API sells stays bookable.
 */
const TILE_SLUGS: Record<TileKind, readonly string[]> = {
  airport: ["airport-transfer"],
  city: ["city-centre-transfer"],
  restaurant: ["dinner-transfer"],
  night: ["nightclub-transfer"],
  agafay: ["agafay-transfer", "agafay-return-transfer"],
  cities: ["essaouira-transfer", "imlil-transfer", "casablanca-transfer", "casablanca-airport-transfer"],
  driver: ["private-driver-half-day", "private-driver-full-day"],
};

export function bareSlug(slug: string): string {
  return slug.replace(/^marrakech-/, "");
}

function namedTile(slug: string): TileKind | null {
  const bare = bareSlug(slug);
  return TILE_KINDS.find((k) => TILE_SLUGS[k].includes(bare)) ?? null;
}

type RouteLike = { slug: string; config: Pick<TransferRouteConfig, "trip" | "secondPlace"> };

/** The tile a route belongs to. Routes no tile names belong to Other cities. */
export function tileForSlug(slug: string): TileKind {
  return namedTile(slug) ?? "cities";
}

/** A tile's routes, in the tile's own order; unnamed routes close Other cities. */
export function tileRoutes<R extends RouteLike>(routes: readonly R[], kind: TileKind): R[] {
  const named = TILE_SLUGS[kind].flatMap((bare) => routes.filter((r) => bareSlug(r.slug) === bare));
  if (kind !== "cities") return named;
  return [...named, ...routes.filter((r) => namedTile(r.slug) === null)];
}

/** The tiles that have at least one route, in tile order, with their routes. */
export function buildTiles<R extends RouteLike>(routes: readonly R[]): Array<{ kind: TileKind; routes: R[] }> {
  return TILE_KINDS.map((kind) => ({ kind, routes: tileRoutes(routes, kind) })).filter((t) => t.routes.length > 0);
}

/** The lowest priced vehicle across the given routes, in dirhams, or null. */
export function lowestPrice(routes: ReadonlyArray<{ packages: ReadonlyArray<{ price: number }> }>): number | null {
  const prices = routes.flatMap((r) => r.packages.map((p) => p.price)).filter((p) => Number.isFinite(p) && p > 0);
  return prices.length > 0 ? Math.min(...prices) : null;
}

/** "Other cities" chips and the driver's length name their route with these keys. */
export function routeChoiceKey(slug: string): string | null {
  switch (bareSlug(slug)) {
    case "essaouira-transfer":
      return "transport.chip_essaouira";
    case "imlil-transfer":
      return "transport.chip_imlil";
    case "casablanca-transfer":
      return "transport.dest_casablanca";
    case "casablanca-airport-transfer":
      return "transport.city_casablanca_airport";
    case "private-driver-half-day":
      return "transport.half_day";
    case "private-driver-full-day":
      return "transport.full_day";
    default:
      return null;
  }
}

/** Keeps `ride` only when it names a tile. */
export function parseTileKind(value: unknown): TileKind | undefined {
  return typeof value === "string" && (TILE_KINDS as readonly string[]).includes(value) ? (value as TileKind) : undefined;
}
